/**
 * Centralized environment configuration
 * All environment-specific values should be defined here
 */

// Environment detection
const mode = import.meta.env.MODE;

export const isLocalDev =
  import.meta.env.DEV && (mode === "development" || mode === "local"); // Running local dev server
export const isProduction = mode === "production" || mode === "prod";
export const isDev = isLocalDev || mode === "dev" || mode === "development";
export const isStaging = mode === "staging" || mode === "stg";

// Helper to get environment-specific values
// Precedence: Production -> Staging -> Dev
function getEnvValue<T>(prod: T, staging: T, dev: T): T {
  if (isProduction) return prod;
  if (isStaging) return staging;
  return dev;
}

const localClientPortalApiBasePath =
  process.env.VITE_LOCAL_CLIENT_PORTAL_API_BASE_PATH || "";

// Current environment name
export const environment = getEnvValue("production", "staging", "dev");

// Cognito configuration
export const cognitoConfig = {
  userPoolId: getEnvValue(
    "us-west-2_iNg77P1re",
    "us-west-2_l7r4B0HA3",
    "us-west-2_uRR3HmjKw",
  ),
  userPoolClientId: getEnvValue(
    "33c1l5ftc0ctuusj86i7ttpm58",
    "4605cvf4cj2i3umkjhkjm7oj7f",
    "151rq8ka83vq0b3cskocgc0qn8",
  ),
  identityPoolId: getEnvValue(
    "us-west-2:6612f00e-ee0d-44a1-ba8d-afb5011fdce4",
    "us-west-2:6de8b7b0-e2ce-4ba3-a59f-acc099b60e4e",
    "us-west-2:e68c8be4-667c-47f1-a5b2-f9c971a1fbce",
  ),
};

// API endpoints
const getClientPortalApiEndpoint = () => {
  if (
    localClientPortalApiBasePath &&
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ) {
    return `${window.location.origin}${localClientPortalApiBasePath}`;
  }

  return getEnvValue(
    "https://client.api.wavvest.com",
    "https://staging.client.api.wavveststg.com",
    "https://dev.client.api.wavvestdev.com",
  );
};

export const apiEndpoints = {
  clientPortal: getClientPortalApiEndpoint(),
  // RIA backend hosts the public firm-branding endpoints (resolve a firm's
  // theme by alias or brand id). These live on the RIA API, NOT the client
  // portal API, so branded login URLs (/firm/:firmId) must target this host.
  ria: getEnvValue(
    "https://ria.api.wavvest.com",
    "https://staging.ria.api.wavveststg.com",
    "https://dev.ria.api.wavvestdev.com",
  ),
  apex: getEnvValue(
    "https://apex.wavvest.com",
    "https://staging.apex.wavveststg.com",
    "https://dev.apex.wavvestdev.com",
  ),
  plaidCash: getEnvValue(
    "https://plaidcash.wavvest.com",
    "https://staging.plaidcash.wavveststg.com",
    "https://dev.plaidcash.wavvestdev.com",
  ),
  identity: getEnvValue(
    "https://identity.wavvest.com",
    "https://staging.identity.wavveststg.com",
    "https://dev.identity.wavvestdev.com",
  ),
  financialPlan: getEnvValue(
    "https://planning.wavvest.com",
    "https://staging.planning.wavveststg.com",
    "https://dev.planning.wavvestdev.com",
  ),
  featureFlags: getEnvValue(
    "https://flags.wavvest.com",
    "https://staging.flags.wavveststg.com",
    "https://dev.flags.wavvestdev.com",
  ),
  insurance: getEnvValue(
    "https://ladder.wavvest.com",
    "https://staging.ladder.wavveststg.com",
    "https://dev.ladder.wavvestdev.com",
  ),
};

// Helper for Client Portal Domain with special local handling
const getClientPortalDomain = () => {
  if (isProduction) return "https://client.wavvest.com";

  if (isStaging) return "https://staging.client.wavveststg.com";

  // Custom local development override
  if (
    isLocalDev &&
    typeof window !== "undefined" &&
    window.location.hostname === "localhost"
  ) {
    return `http://localhost:${window.location.port}`;
  }

  return "https://dev.client.wavvestdev.com";
};

// Domain URLs
export const domains = {
  clientPortal: getClientPortalDomain(),
  signup: getEnvValue(
    "https://signup.wavvest.com",
    "https://signup.wavveststg.com",
    "https://signup.wavvestdev.com",
  ),
};

// App title suffix based on environment
export const appTitleSuffix = getEnvValue("", "-Stg", "-Dev");

// Region (consistent across all environments)
export const awsRegion = "us-west-2";
